import React, {
  useMemo,
  useRef,
  useState,
  useContext,
  useEffect,
  Context,
  ComponentType,
  ReactNode,
} from 'react';

import {
  initialize,
  LDClient,
  LDContext,
  LDOptions,
} from 'launchdarkly-js-client-sdk';
import PropTypes, { Validator } from 'prop-types';

import { getRegion } from '@adsk/acs-sc-web-platform-api';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { useUser } from './ProjectEntitlementsContext';

export type LaunchDarklyContextValue = {
  client: {
    current: LDClient | null;
  };
  loading: boolean;
};

type SimpleLaunchDarklyProviderProps = {
  apiKey: string;
  user?: LDContext;
  options?: LDOptions;
  children?: ReactNode;
};

export type LaunchDarklyProviderProps = {
  apiKey: string;
  projectEntitlementsUnavailable?: boolean;
  children?: ReactNode;
  customUserAttrs?: LDContext['custom'];
};

export const DEFAULT_ANONYMOUS_USER_ID = 'nonuser' as const;

const customAttrValue = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.bool,
  PropTypes.array,
]);

const LDUserCustom = PropTypes.objectOf(customAttrValue) as Validator<
  LDContext['custom']
>;

const LDUserShape = PropTypes.shape({
  /**
   * A unique string identifying a user.
   *
   * If you omit this property, and also set `anonymous` to `true`, the SDK will generate a UUID string
   * and use that as the key; it will attempt to persist that value in local storage if possible so the
   * next anonymous user will get the same key, but if local storage is unavailable then it will
   * generate a new key each time you specify the user.
   *
   * It is an error to omit the `key` property if `anonymous` is not set.
   */
  key: PropTypes.string,

  /**
   * @deprecated - There is no longer such a thing as a secondary meta-attribute that affects percentage. If you set an attribute with that name in LDContext, it will simply be a custom attribute like any other.
   */
  secondary: PropTypes.string,

  /**
   * The user's name.
   *
   * You can search for users on the User page by name.
   */
  name: PropTypes.string,

  /**
   * The user's first name.
   */
  firstName: PropTypes.string,

  /**
   * The user's last name.
   */
  lastName: PropTypes.string,

  /**
   * The user's email address.
   *
   * If an `avatar` URL is not provided, LaunchDarkly will use Gravatar
   * to try to display an avatar for the user on the Users page.
   */
  email: PropTypes.string,

  /**
   * An absolute URL to an avatar image for the user.
   */
  avatar: PropTypes.string,

  /**
   * The user's IP address.
   */
  ip: PropTypes.string,

  /**
   * The country associated with the user.
   */
  country: PropTypes.string,

  /**
   * Whether to show the user on the Users page in LaunchDarkly.
   */
  anonymous: PropTypes.bool,

  /**
   * Any additional attributes associated with the user.
   */
  custom: LDUserCustom,

  /**
   * Specifies a list of attribute names (either built-in or custom) which should be
   * marked as private, and not sent to LaunchDarkly in analytics events. This is in
   * addition to any private attributes designated in the global configuration
   * with [[LDOptions.privateAttributes]] or [[LDOptions.allAttributesPrivate]].
   */
  privateAttributes: PropTypes.arrayOf(PropTypes.string),
}) as Validator<LDContext>;

/**
 * This method creates a new LaunchDarkly providers & hooks.
 * We need this method in order to have multiple contexts in a single page (one for Web Platform, one for RFIs, etc.)
 * That's the reason for creating `LaunchDarklyContext` inside this method (or using the provided one), instead of globally.
 * See example for how we generate this in ./AlloyLaunchDarklyProvider.js
 *
 * @returns {{ SimpleLaunchDarklyProvider, LaunchDarklyProvider, useFeatureFlag, useAllFeatureFlags, }}
 */
const createLaunchDarklyContext = (
  context?: Context<LaunchDarklyContextValue | undefined>,
) => {
  const LaunchDarklyContext =
    context ||
    React.createContext<LaunchDarklyContextValue | undefined>(undefined);

  /**
   * Simple Launch Darkly provider without expecting any other provider to exist.
   *
   * @param apiKey
   * @param user
   * @param options
   * @param children
   * @constructor
   */
  const SimpleLaunchDarklyProvider: ComponentType<
    SimpleLaunchDarklyProviderProps
  > = ({ apiKey, user, options, children }) => {
    const client = useRef<LDClient | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let cancel = false;
      const endLoadingIFNeeded = () => {
        if (!cancel) {
          setLoading(false);
        }
      };
      if (user) {
        if (client.current === null) {
          client.current = initialize(apiKey, user, options);
          client.current.on('ready', endLoadingIFNeeded);
        } else {
          if (!cancel) {
            setLoading(true);
            client.current.identify(user, undefined, endLoadingIFNeeded);
          }
        }
      }
      return () => {
        cancel = true;
      };
    }, [apiKey, user, options]);

    const memoizedValue = useMemo(
      () => ({ loading, client }),
      [loading, client],
    );
    return (
      <LaunchDarklyContext.Provider value={memoizedValue}>
        {children}
      </LaunchDarklyContext.Provider>
    );
  };

  SimpleLaunchDarklyProvider.propTypes = {
    /**
     * LaunchDarkly API key
     */
    apiKey: PropTypes.string.isRequired,
    /**
     * The Launch Darkly user
     */
    user: LDUserShape,
    /**
     * The Launch Darkly client options
     * See: https://docs.launchdarkly.com/sdk/client-side/javascript#initializing-the-client
     */
    options: PropTypes.object,
    /**
     * Children
     */
    children: PropTypes.node,
  };

  /**
   * Launch Darkly Provider that expected to be inside `AlloyProvider` and uses this data to initiate LD calls.
   *
   * @param apiKey
   * @param projectEntitlementsUnavailable
   * @param children
   * @param customUserAttrs
   * @constructor
   */
  const LaunchDarklyProvider: ComponentType<LaunchDarklyProviderProps> = ({
    apiKey,
    projectEntitlementsUnavailable,
    children,
    customUserAttrs,
  }) => {
    const projectId = useCurrentProjectId();
    const accountId = useCurrentAccountId();
    const { user } = useUser();
    const { analyticsId, email } = user || {};
    const region = getRegion();
    const dataReady =
      region &&
      user &&
      (accountId || projectId || projectEntitlementsUnavailable);
    const ldUser = useMemo(() => {
      if (!dataReady) {
        return undefined;
      }
      const custom = customUserAttrs ?? {};
      if (region) {
        Object.assign(custom, { region });
      }
      if (accountId) {
        Object.assign(custom, { accountId });
      }
      if (projectId) {
        Object.assign(custom, { projectId });
      }
      return {
        kind: 'user',
        key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
        anonymous: true,
        email,
        ...custom,
      };
    }, [
      dataReady,
      customUserAttrs,
      region,
      accountId,
      projectId,
      analyticsId,
      email,
    ]);
    const options = useMemo(
      () => ({
        allAttributesPrivate: true,
        sendEventsOnlyForVariation: true,
      }),
      [],
    );

    return (
      <SimpleLaunchDarklyProvider
        user={ldUser}
        apiKey={apiKey}
        options={options}
      >
        {children}
      </SimpleLaunchDarklyProvider>
    );
  };

  LaunchDarklyProvider.defaultProps = {
    projectEntitlementsUnavailable: false,
  };

  LaunchDarklyProvider.propTypes = {
    /**
     * LaunchDarkly API key
     */
    apiKey: PropTypes.string.isRequired,
    /**
     * Whether or not project entitlements are unavailable
     */
    projectEntitlementsUnavailable: PropTypes.bool,
    /**
     * Children
     */
    children: PropTypes.node.isRequired,
    /**
     * Initial object to initialize the user custom data
     */
    customUserAttrs: LDUserCustom,
  };

  /**
   *
   * @param flagName
   * @param defaultValue
   * @returns {{loading, value: *}}
   */
  const useFeatureFlag = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const ldContext = useContext(LaunchDarklyContext);
    if (!ldContext) {
      return {
        loading: false,
        value: defaultValue,
      };
    }
    const { loading, client } = ldContext;
    const value = loading
      ? defaultValue
      : client.current?.variation(flagName, defaultValue);
    return { loading, value };
  };

  /**
   *
   * @returns {{loading, value: *}}
   */
  const useAllFeatureFlags = () => {
    const ldContext = useContext(LaunchDarklyContext);
    if (!ldContext) {
      return {
        loading: false,
        value: {},
      };
    }
    const { loading, client } = ldContext;
    const value = loading ? {} : client.current?.allFlags();
    return { loading, value };
  };

  return {
    SimpleLaunchDarklyProvider,
    LaunchDarklyProvider,
    useFeatureFlag,
    useAllFeatureFlags,
  };
};

export default createLaunchDarklyContext;
